import { useEffect, useRef } from 'react';

import { useDeliveryLocationCookie } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocationCookie';
import { NOT_APPLICABLE } from '@/data/constants/gtm';
import { usePlpEstimatedDeliveryStates } from '@/data/Content/PlpEstimatedDeliveryStates';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';

import { TrackItemDeliveryPLPProps } from './types';
import { useTrackItemDeliveryPLPGA4 } from './useTrackItemDeliveryPLPGA4';
import { getEddDaysValue } from './utils';

export const useTrackItemDeliveryPLP = ({ products }: TrackItemDeliveryPLPProps) => {
	const { states, isResolved } = usePlpEstimatedDeliveryStates(products);
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { zipCode } = useDeliveryLocationCookie();
	const sendGA4Event = useTrackItemDeliveryPLPGA4();

	const trackedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (!products?.length || !physicalStoreId || !isResolved) return;

		const items = products.map((product) => {
			const state = states.get(product.id);
			return {
				item_id: getItemPartNumber(product),
				estimated_delivery_days: state ? getEddDaysValue(state.delivery) : NOT_APPLICABLE,
				pick_up_days: state ? getEddDaysValue(state.pickup) : NOT_APPLICABLE,
				...(state && state.delivery.status === 'same-day' && getEddDaysValue(state.delivery) === '0'
					? { express_delivery: 'true' }
					: {}),
			};
		});

		const trackingKey = `${items
			.map((item) => item.item_id)
			.join(',')}|${physicalStoreId}|${zipCode}`;
		if (trackedKeyRef.current === trackingKey) return;
		trackedKeyRef.current = trackingKey;

		sendGA4Event({ items });
	}, [products, states, isResolved, physicalStoreId, zipCode, sendGA4Event]);
};
